
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Mic, MicOff, Send, Calendar, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { CalendarEvent, DateTimeRange } from '@/types/calendar';
import { generateScheduleSuggestions } from './aiScheduler';

interface AIEventPromptProps {
  events: CalendarEvent[];
  onEventAdd: (event: CalendarEvent) => void;
}

const AIEventPrompt: React.FC<AIEventPromptProps> = ({ onEventAdd, events }) => {
  const [prompt, setPrompt] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOpen, setIsOpen] = useState(true); // Changed to true to make it visible by default
  const [speechRecognition, setSpeechRecognition] = useState<SpeechRecognition | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';
        
        recognition.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          setPrompt(transcript);
          setIsRecording(false);
        };
        
        recognition.onerror = (event) => {
          console.error('Speech recognition error', event.error);
          toast.error('Failed to recognize speech. Please try again or type your request.');
          setIsRecording(false);
        };
        
        recognition.onend = () => {
          setIsRecording(false);
        };
        
        setSpeechRecognition(recognition);
      }
    }
    
    return () => {
      if (speechRecognition) {
        speechRecognition.abort();
      }
    };
  }, []);

  const toggleRecording = () => {
    if (!speechRecognition) {
      toast.error('Speech recognition is not supported in your browser');
      return;
    }
    
    if (isRecording) {
      speechRecognition.stop();
      setIsRecording(false);
    } else {
      setPrompt('');
      speechRecognition.start();
      setIsRecording(true);
    }
  };

  const handleSubmit = async () => {
    if (!prompt.trim()) return;
    
    setIsProcessing(true);
    
    try {
      const eventInfo = await processNaturalLanguagePrompt(prompt, events);
      
      if (eventInfo) {
        onEventAdd(eventInfo);
        toast.success('Event successfully scheduled!');
        setPrompt('');
      }
    } catch (error) {
      console.error('Error processing prompt:', error);
      toast.error('Failed to process your request. Please try again with more details.');
    } finally {
      setIsProcessing(false);
    }
  };

  const processNaturalLanguagePrompt = async (text: string, existingEvents: CalendarEvent[]): Promise<CalendarEvent | null> => {
    const isMeeting = /meeting|call|conference|discuss|talk|chat/i.test(text);
    const isDeadline = /deadline|due|by|finish/i.test(text);
    const isAppointment = /appointment|doctor|dentist|haircut|visit/i.test(text);
    
    let eventType = 'other';
    if (isMeeting) eventType = 'meeting';
    else if (isDeadline) eventType = 'deadline';
    else if (isAppointment) eventType = 'appointment';
    
    const hasTimeConstraint = /before|after|next|tomorrow|tonight|today|this week|weekend/i.test(text);
    
    const durationGuess = isMeeting ? 60 : isAppointment ? 90 : 30;
    
    const suggestion = await generateScheduleSuggestions(
      existingEvents,
      {
        title: text,
        type: eventType,
        description: text,
        durationMinutes: durationGuess,
        participants: [],
        preferredDays: [],
        preferredTimeRange: 'any'
      }
    );
    
    if (suggestion.suggestedTimeSlots.length === 0) {
      toast.error('Could not find a suitable time slot. Please try a different request.');
      return null;
    }
    
    const timeSlot = suggestion.suggestedTimeSlots[0];
    
    const newEvent: CalendarEvent = {
      id: `event-${Date.now()}`,
      title: extractTitle(text),
      description: text,
      start: timeSlot.start,
      end: timeSlot.end,
      location: extractLocation(text),
      source: 'local',
      calendarId: 'local',
      attendees: [] // Initialize with empty array matching the CalendarEvent type
    };
    
    return newEvent;
  };

  const extractTitle = (text: string): string => {
    const firstSentence = text.split(/[.!?]/, 1)[0].trim();
    return firstSentence.length > 50 ? firstSentence.substring(0, 47) + '...' : firstSentence;
  };

  const extractLocation = (text: string): string | undefined => {
    const locationMatches = text.match(/at\s+([^,.]+)|in\s+([^,.]+)/i);
    return locationMatches ? (locationMatches[1] || locationMatches[2]) : undefined;
  };

  useEffect(() => {
    if (!isRecording && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isRecording]);

  if (!isOpen) {
    return null;
  }

  return (
    <Card className="overflow-hidden border-primary/20 shadow-lg hover:shadow-primary/5 transition-all">
      <CardContent className="p-0">
        <div className="relative bg-gradient-to-r from-purple-500/10 to-blue-500/10 backdrop-blur-sm p-4 border-b">
          <div className="absolute right-0 top-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl -z-10"></div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Calendar Assistant
          </h3>
          <p className="text-sm text-muted-foreground">Describe what you want to schedule in natural language</p>
        </div>
        
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-2">
            <Input
              ref={inputRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., 'Schedule a haircut before next Saturday' or 'Meeting with Sarah tomorrow afternoon'"
              className="flex-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              disabled={isRecording || isProcessing}
            />
            <Button 
              size="icon" 
              variant={isRecording ? "destructive" : "outline"} 
              onClick={toggleRecording}
              disabled={isProcessing}
              className="relative"
            >
              {isRecording ? (
                <MicOff className="h-4 w-4" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
              {isRecording && (
                <span className="absolute top-0 right-0 h-3 w-3 rounded-full bg-red-500 animate-pulse"></span>
              )}
            </Button>
            <Button 
              size="icon" 
              onClick={handleSubmit} 
              disabled={!prompt.trim() || isProcessing || isRecording}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground italic">
            {isRecording ? (
              <span className="text-primary animate-pulse">Listening... Speak now</span>
            ) : (
              "Try saying or typing something like: 'Schedule a team meeting next Tuesday at 2pm' or 'Remind me to call mom this weekend'"
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default AIEventPrompt;
